/**
 * Money unit helpers.
 *
 * Convention across this codebase: monetary values are stored and computed as
 * integer cents. Conversion happens at external boundaries — Shopify's APIs
 * return dollar decimal strings, Stripe/PayPal constants are documented in
 * dollars, and the admin/partner UIs display dollars to users.
 *
 * "Cents" here means HUNDREDTHS OF THE MAJOR UNIT, for every currency — the
 * conversions below take no currency argument, so a ¥12,355 order is stored as
 * 1235500 exactly as a $12,355 one is. That is internally consistent and the
 * display path cancels it out cleanly, including for JPY.
 *
 * It is NOT the same thing as the currency's own minor unit, which is the yen
 * for JPY and the fil (1/1000) for KWD. The two coincide only for a 1/100
 * currency, so anything handing an amount to a provider that speaks real minor
 * units must convert — see `centsToMinorUnits` (MPPRO-133).
 */

/**
 * Convert a Shopify-style dollar decimal string (e.g. "100.00") to integer cents.
 * Falls back to 0 when the input is undefined/null/empty.
 */
export const toCents = (dollarString: string | undefined | null): number =>
  Math.round(parseFloat(dollarString || '0') * 100);

/**
 * Convert a dollar float to integer cents.
 *
 * The string-input `toCents` above covers every Shopify boundary, so this is
 * for the rarer case of an amount that is already a number in dollars — a
 * provider's decimal figure, as in `payout/stripe.ts`.
 *
 * It is NOT a storage compat shim, which is what it was introduced as: the
 * last dollar-valued stored field, `ProductVariant.price`, became integer
 * cents in MPPRO-45, and `ordersCreateEvent` no longer calls this. Reaching
 * for it on a value read out of DynamoDB now multiplies cents by 100 — the
 * MPPRO-127 defect exactly.
 */
export const dollarsToCents = (dollars: number): number => Math.round(dollars * 100);

/**
 * Convert integer cents to a Shopify-style dollar decimal string ("100.00").
 * Used for outbound sync to Shopify and UI display.
 */
export const centsToDollarString = (cents: number): string => (cents / 100).toFixed(2);

/**
 * Convert integer cents to a dollar number, for display formatters that take a
 * numeric amount (`Intl.NumberFormat` and the apps' `formatCurrency`).
 *
 * This is the numeric sibling of `centsToDollarString`, which exists for
 * Shopify's dollar-decimal string contract. Both apps' `formatCents` helpers
 * delegate here so the divide-by-100 has exactly one definition.
 */
export const centsToDollars = (cents: number): number => cents / 100;

/**
 * The number of decimal places a currency's *provider* minor unit has —
 * its ISO 4217 exponent. USD is 2 (cents), JPY is 0 (the yen has no minor
 * unit at all), KWD is 3 (fils).
 *
 * Derived from `Intl.NumberFormat`, not a hand-maintained table: ICU already
 * ships the exponent for every currency it knows, and a literal list in this
 * repo would be one more thing to keep current.
 *
 * Note what this is NOT: it is not the exponent of the stored value. Storage
 * in this codebase is always hundredths of the MAJOR unit regardless of
 * currency (see `toCents`), which is why this only matters at a provider
 * boundary — see `centsToMinorUnits`.
 *
 * A malformed code (anything ICU will not accept as a currency) falls back to
 * 2, which makes the conversions below the identity and so preserves exactly
 * the behaviour that predates this function. Stripe and PayPal both reject an
 * unknown currency themselves, so this degrades to today's error rather than
 * inventing a new one inside a payout.
 */
export const currencyExponent = (currencyCode: string): number => {
  try {
    // `maximumFractionDigits` is typed optional because it is absent for some
    // notation options; with `style: 'currency'` ICU always resolves it. The
    // `?? 2` is the same identity-preserving fallback as the catch below.
    return (
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
};

/**
 * Convert a stored amount to the currency's true minor unit, for providers
 * that take the smallest currency unit — Stripe's `amount`, above all.
 *
 * Storage holds hundredths of the major unit for EVERY currency, because
 * `toCents` multiplies by 100 with no currency argument. For a 1/100 currency
 * that happens to coincide with the minor unit, so USD passes through
 * unchanged and this is a no-op on every path that exists today. For any other
 * currency it does not coincide, and sending the stored value raw is a
 * straight power-of-ten error:
 *
 *   ¥12,355 is stored as 1235500. Sent to Stripe raw that is ¥1,235,500 —
 *   a 100x OVERPAYMENT, the same shape as MPPRO-127.
 *   KWD 12.36 is stored as 1236. Sent raw that is 1236 fils = KWD 1.236 —
 *   a 10x UNDERPAYMENT, the same class of bug in the other direction.
 */
export const centsToMinorUnits = (cents: number, currencyCode: string): number =>
  Math.round((cents * 10 ** currencyExponent(currencyCode)) / 100);

/**
 * The inverse of `centsToMinorUnits`: take an amount a provider gave us in the
 * currency's smallest unit and express it in the stored unit.
 *
 * Needed wherever a provider's own figure enters the app rather than leaving
 * it — Stripe's balance endpoint reports in minor units, so the same exponent
 * that was missing on the way out was missing on the way back in.
 *
 * Lossy in one direction only, and only for a three-decimal currency: 12355
 * fils is KWD 12.355, which the stored hundredths cannot hold and which rounds
 * to 1236. That loss is the storage representation's, not this function's —
 * `toCents` already rounds the same third decimal away at ingestion.
 */
export const minorUnitsToCents = (minorUnits: number, currencyCode: string): number =>
  Math.round((minorUnits * 100) / 10 ** currencyExponent(currencyCode));

/**
 * Convert a stored amount to a decimal string in the currency's MAJOR unit,
 * with the number of decimal places that currency actually has.
 *
 * This is `centsToDollarString` made currency-aware, for PayPal's Payouts API,
 * which takes major units as a string and validates the decimals against the
 * currency: a JPY amount must carry none, so the fixed `.toFixed(2)` produces
 * "12355.00" and PayPal rejects the payout outright. That is a louder failure
 * than the Stripe one — it does not move the wrong amount of money, it moves
 * none — but it is the same missing exponent.
 */
export const centsToMajorUnitString = (cents: number, currencyCode: string): string =>
  (cents / 100).toFixed(currencyExponent(currencyCode));
